import { readonly, ref } from 'vue';
import type { AvailabilityCalendarOptions, AvailabilityPromptContext, AvailabilityQuickAddContext, AvailabilitySuggestion } from '@/types/availability';

interface PromptMatchData extends Record<string, unknown> {
	matchDate?: string | Date | null;
	matchTime?: string | null;
	matchNumber?: number | null;
	homeTeam?: string | null;
	awayTeam?: string | null;
	venue?: string | null;
	field?: string | null;
	level?: string | null;
	league?: string | null;
	fee?: number | string | null;
	assignedPosition?: string | null;
	location?: { name?: string | null } | null;
	client?: { name?: string | null; clientNumber?: number | null } | null;
	group?: { name?: string | null; shortName?: string | null; groupNumber?: number | null } | null;
}

const DEFAULT_PROMPT_CONTEXT: Required<Omit<AvailabilityPromptContext, 'defaultQuickAdd'>> & {
	defaultQuickAdd: AvailabilityQuickAddContext | null;
} = {
	mode: 'assignment',
	title: null,
	message: null,
	defaultQuickAdd: null
};

const DEFAULT_CALENDAR_OPTIONS: Required<AvailabilityCalendarOptions> = {
	initialDate: null,
	initialView: 'month',
	selectedDates: []
};

// Singleton state - shared across all component instances
const isCalendarOpen = ref(false);
const isPromptOpen = ref(false);
const promptMatchData = ref<PromptMatchData | null>(null);
const promptSuggestions = ref<AvailabilitySuggestion[]>([]);
const promptContext = ref({ ...DEFAULT_PROMPT_CONTEXT });
const calendarOptions = ref({ ...DEFAULT_CALENDAR_OPTIONS });

export function useAvailabilityModal() {
	function openCalendar(options: AvailabilityCalendarOptions = {}) {
		calendarOptions.value = {
			initialDate: options.initialDate ?? null,
			initialView: options.initialView ?? 'month',
			selectedDates: options.selectedDates ?? []
		};
		isCalendarOpen.value = true;
		isPromptOpen.value = false;
	}

	function closeCalendar() {
		isCalendarOpen.value = false;
		calendarOptions.value = { ...DEFAULT_CALENDAR_OPTIONS };
	}

	function openPrompt(matchData: PromptMatchData, suggestions: AvailabilitySuggestion[] = [], context: AvailabilityPromptContext = {}) {
		promptMatchData.value = matchData;
		promptSuggestions.value = suggestions;
		promptContext.value = {
			mode: context.mode ?? 'assignment',
			title: context.title ?? null,
			message: context.message ?? null,
			defaultQuickAdd: context.defaultQuickAdd ?? null
		};
		isPromptOpen.value = true;
	}

	function closePrompt() {
		isPromptOpen.value = false;
		promptMatchData.value = null;
		promptSuggestions.value = [];
		promptContext.value = { ...DEFAULT_PROMPT_CONTEXT };
	}

	function closeAll() {
		closeCalendar();
		closePrompt();
	}

	function toggleCalendar() {
		if (isCalendarOpen.value) {
			closeCalendar();
			return;
		}

		openCalendar();
	}

	return {
		isCalendarOpen: readonly(isCalendarOpen),
		isPromptOpen: readonly(isPromptOpen),
		promptMatchData: readonly(promptMatchData),
		promptSuggestions: readonly(promptSuggestions),
		promptContext: readonly(promptContext),
		calendarOptions: readonly(calendarOptions),
		openCalendar,
		closeCalendar,
		toggleCalendar,
		openPrompt,
		closePrompt,
		closeAll
	};
}

export { calendarOptions, isCalendarOpen, isPromptOpen, promptContext, promptMatchData, promptSuggestions };
